/* Phase 50 + extension — Floating "Contribute now" mini-button.
 *
 * Appears after the user has scrolled past the social-proof widget on home,
 * or whenever they're on any non-impact page that's been scrolled enough.
 * Single tap navigates to the current featured issue's donate flow with
 * ?donate=1 so the donate modal opens automatically.
 *
 * Behaviors:
 *  - Hidden on all /impact/* and /login* /register* pages
 *  - Hidden when no active featured issue exists
 *  - Hidden when the user dismissed it within the last 7 days (localStorage)
 *  - Slides up from bottom-right; positioned ABOVE the WhatsApp launcher
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Heart, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const SHOW_AFTER_SCROLL_PX = 600;
const DISMISS_KEY = 'ibclub_contribute_dismissed_at';
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000;  // 7 days

const FloatingContributeButton = () => {
  const { pathname } = useLocation();
  const [issue, setIssue] = useState(null);
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [expanded, setExpanded] = useState(false);

  // Route exclusions
  const hidden = useMemo(() => {
    const p = pathname || '';
    return (
      p.startsWith('/impact')
      || p.startsWith('/login')
      || p.startsWith('/register')
      || p.startsWith('/admin')
      || p.startsWith('/dashboard')
      || p === '/checkout'
    );
  }, [pathname]);

  // Persistent dismissal
  useEffect(() => {
    try {
      const t = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
      if (t && Date.now() - t < DISMISS_TTL_MS) setDismissed(true);
    } catch { /* ignore */ }
  }, []);

  // Fetch a featured/active issue once
  useEffect(() => {
    if (hidden || dismissed) return;
    let active = true;
    (async () => {
      try {
        // Featured first
        let { data } = await axios.get(`${API}/impact/issues`, { params: { featured_only: true, limit: 1 } });
        if (!data.issues?.length) {
          ({ data } = await axios.get(`${API}/impact/issues`, { params: { limit: 1 } }));
        }
        if (!active) return;
        const candidate = (data.issues || []).find(
          (i) => (i.help_types || []).includes('financial') && ['active', 'in_progress'].includes(i.status),
        );
        setIssue(candidate || null);
      } catch { /* silent */ }
    })();
    return () => { active = false; };
  }, [hidden, dismissed]);

  // Show on scroll past threshold
  useEffect(() => {
    if (hidden || dismissed || !issue) { setVisible(false); return; }
    const onScroll = () => setVisible(window.scrollY > SHOW_AFTER_SCROLL_PX);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [hidden, dismissed, issue]);

  const dismiss = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
    setDismissed(true);
  };

  if (hidden || dismissed || !issue || !visible) return null;

  return (
    <AnimatePresence>
      <motion.div
        key="floating-contribute"
        initial={{ opacity: 0, y: 20, scale: 0.9 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 20, scale: 0.9 }}
        transition={{ type: 'spring', stiffness: 380, damping: 28 }}
        className="fixed z-40"
        style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 88px)', right: '16px' }}
        data-testid="floating-contribute"
      >
        {expanded ? (
          <div
            className="bg-slate-900 border border-[#d4af37]/40 shadow-2xl max-w-[300px]"
            data-testid="floating-contribute-expanded"
          >
            <div className="p-4 pb-3">
              <div className="flex items-start justify-between gap-2 mb-2">
                <span className="text-[10px] uppercase tracking-widest text-[#d4af37] flex items-center gap-1">
                  <Heart size={11} fill="currentColor" /> Impact Hub
                </span>
                <button
                  onClick={dismiss}
                  className="text-slate-500 hover:text-white -mt-1 -mr-1 p-1"
                  aria-label="Dismiss"
                  data-testid="floating-contribute-dismiss"
                >
                  <X size={14} />
                </button>
              </div>
              <p className="text-sm text-white font-medium leading-snug mb-1 line-clamp-2">{issue.title}</p>
              {issue.city && (
                <p className="text-[11px] text-slate-500 mb-3">
                  {issue.city}{issue.state ? `, ${issue.state}` : ''}
                </p>
              )}
              <Link
                to={`/impact/${issue.slug}?donate=1`}
                onClick={() => setExpanded(false)}
                className="block bg-[#d4af37] hover:bg-[#e5c558] text-slate-950 font-semibold text-sm text-center py-2 transition-colors"
                data-testid="floating-contribute-cta"
              >
                Contribute now →
              </Link>
              <Link
                to="/impact"
                onClick={() => setExpanded(false)}
                className="block text-center text-[11px] text-slate-400 hover:text-[#d4af37] mt-2"
                data-testid="floating-contribute-browse"
              >
                Browse all initiatives
              </Link>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setExpanded(true)}
            className="group bg-[#d4af37] hover:bg-[#e5c558] text-slate-950 font-semibold flex items-center gap-2 px-4 py-3 shadow-2xl hover:shadow-[#d4af37]/30 transition-all rounded-full"
            data-testid="floating-contribute-trigger"
            aria-label="Contribute to a cause"
          >
            <Heart size={16} fill="currentColor" className="group-hover:scale-110 transition-transform" />
            <span className="text-sm whitespace-nowrap">Contribute</span>
          </button>
        )}
      </motion.div>
    </AnimatePresence>
  );
};

export default FloatingContributeButton;
